import { styled } from '@linaria/react';
import { Flex } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { truncateLabel } from '../../helpers/string-helpers/string-helpers';
import { Checkbox } from '../../ui-library/checkbox/checkbox';
import { Tooltip } from '../../ui-library/tooltip/tooltip';
import { Text } from '../../ui-library/typography/typography';

interface Props {
  value: string;
  label: string | null;
  color: string;
  isHovered?: boolean;
  isChecked: boolean;
  /**
   * Whether this item has any data to show. Omit when the concept doesn't
   * apply to the consumer — existing behavior (always toggleable, tooltip
   * only on truncation) is unchanged when this is undefined.
   */
  hasData?: boolean;
  /**
   * Max characters before the label is truncated (with the full text moved to
   * the hover tooltip). Defaults to 12 to keep the compact legends the strategy
   * and work-period charts rely on; raise it for legends whose labels ARE the
   * data (e.g. model-family names) and shouldn't clip.
   */
  maxLabelLength?: number;
  onMouseEnter?: (itemId: string) => void;
  onMouseLeave?: () => void;
  onToggle: (itemId: string) => void;
}

export const LineChartLegendItemWithCheckbox = ({
  value,
  label,
  color,
  isHovered,
  isChecked,
  hasData,
  maxLabelLength = 12,
  onMouseEnter,
  onMouseLeave,
  onToggle,
}: Props) => {
  const { t } = useTranslation();
  const labelText = label || '';
  const noData = hasData === false;
  const isTruncated = labelText.length > maxLabelLength;
  const showTooltip = isTruncated || noData;
  // Assistive tech always hears the no-data reason for a no-data item; the
  // visual tooltip, though, prefers the full label whenever it's truncated —
  // otherwise a long no-data label would clip on the axis with no way to read
  // it. Only a non-truncated no-data item falls back to the no-data string.
  const ariaLabel = noData ? t('common.labels.no_data_for_item') : labelText;
  const tooltipLabel = isTruncated ? labelText : ariaLabel;

  return (
    <Tooltip
      label={tooltipLabel}
      position="top"
      variant="white"
      disabled={!showTooltip}
      multiline
      maw={280}
      w="max-content"
    >
      <Flex
        key={value}
        align="center"
        gap={8}
        style={{
          cursor: noData ? 'default' : 'pointer',
          opacity: isHovered === undefined ? 1 : isHovered ? 1 : 0.3,
          transition: 'opacity 0.2s ease',
        }}
        onMouseEnter={() => onMouseEnter?.(value)}
        onMouseLeave={() => onMouseLeave?.()}
        onClick={(e) => {
          e.stopPropagation();
          if (!noData) onToggle(value);
        }}
      >
        <Checkbox checked={isChecked} readOnly disabled={noData} aria-label={ariaLabel} />
        <Label color={color}>
          <Text size="small" lineHeight="none">
            {truncateLabel(labelText, maxLabelLength)}
          </Text>
        </Label>
      </Flex>
    </Tooltip>
  );
};

const Label = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  gap: 6px;

  &::before {
    content: '';
    min-width: 10px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${(props) => props.color};
  }
`;
